/**
 * Small charts and lists shared by the report panels: a source's colour
 * badge, a sparkline for a folded panel, a bar list (bar length = value)
 * for keywords, channels and campaigns, and a quiet list for the figures
 * a panel demotes under its chart. Flat: no tiles, no borders (DD-002,
 * DD-011).
 */

import React, { ReactNode } from "react";

/** One colour per source, the pace charts' colours (validated as a set
 *  against the light surface with the dataviz validator). Paid is amber on
 *  both Meta and Google Ads: never in one chart, and the same job. */
export const SOURCE_COLOR = {
  social: "#2563EB",
  businessProfile: "#059669",
  website: "#6366F1",
  paid: "#D97706",
} as const;

/** The panel icon on a 10% wash of its source's colour. */
export const SourceBadge: React.FC<{ color: string; children: ReactNode }> = ({
  color,
  children,
}) => (
  <span
    className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-md"
    style={{ backgroundColor: `${color}1A`, color }}
  >
    {children}
  </span>
);

/** A panel's main chart at `height` px, plus the slack of its canvas tile:
 *  a tile hands its spare height to the charts marked by this box through
 *  `--chart-grow` (ReportPanel), so a chart fills its tile and the rail and
 *  the phone's list keep it at `height`. Its child sizes to 100% of it. */
export const ChartBox: React.FC<{ height: number; className?: string; children: ReactNode }> = ({
  height,
  className,
  children,
}) => (
  <div data-chart-grow="" className={className} style={{ height: `calc(${height}px + var(--chart-grow, 0px))` }}>
    {children}
  </div>
);

const SPARK_WIDTH = 128;
const SPARK_HEIGHT = 36;
const SPARK_PAD = 4;

// Under the plot, each campaign's 2px line in its own row.
const SPAN_GAP = 3;
const SPAN_ROW = 4;
const TICK_HEIGHT = 5;

/** A single series as a 2px line over a 10% wash, its last point a dot
 *  with a surface ring. A null is a gap, never a zero (DD-021): the line
 *  breaks there. Fewer than two points draw nothing. Optional marks, read
 *  only: `spans`, a campaign's days as a 2px paid line under the plot, one
 *  row each; `ticks`, a day's 2px tick on the baseline. Both in indexes of
 *  `values`, clipped to them. */
export const Sparkline: React.FC<{
  values: ReadonlyArray<number | null>;
  color: string;
  /** What the line is, read on hover and by screen readers. */
  title: string;
  spans?: ReadonlyArray<{ key: string; from: number; to: number }>;
  ticks?: ReadonlyArray<{ key: string; at: number; color: string }>;
}> = ({ values, color, title, spans = [], ticks = [] }) => {
  const known = values.filter((v): v is number => v != null);
  if (known.length < 2) return null;
  const min = Math.min(...known);
  const max = Math.max(...known);
  const span = max - min || 1;
  const lastIndex = values.length - 1;
  const clip = (i: number) => Math.min(Math.max(i, 0), lastIndex);
  const x = (i: number) =>
    SPARK_PAD + (i / Math.max(values.length - 1, 1)) * (SPARK_WIDTH - SPARK_PAD * 2);
  const y = (v: number) =>
    SPARK_HEIGHT - SPARK_PAD - ((v - min) / span) * (SPARK_HEIGHT - SPARK_PAD * 2);
  const height = SPARK_HEIGHT + (spans.length > 0 ? SPAN_GAP + spans.length * SPAN_ROW - 2 : 0);
  // Runs of known points; a gap starts a new run.
  const runs: Array<Array<[number, number]>> = [];
  values.forEach((v, i) => {
    if (v == null) {
      runs.push([]);
      return;
    }
    if (runs.length === 0) runs.push([]);
    runs[runs.length - 1].push([x(i), y(v)]);
  });
  const lastKnown = values.reduce<number>((last, v, i) => (v != null ? i : last), -1);
  const lastValue = values[lastKnown] as number;
  return (
    <svg
      width={SPARK_WIDTH}
      height={height}
      viewBox={`0 0 ${SPARK_WIDTH} ${height}`}
      role="img"
      aria-label={title}
      className="block overflow-visible"
    >
      <title>{title}</title>
      {runs
        .filter((run) => run.length > 1)
        .map((run, i) => {
          const line = run.map(([px, py]) => `${px},${py}`).join(" ");
          const area = `${run[0][0]},${SPARK_HEIGHT} ${line} ${run[run.length - 1][0]},${SPARK_HEIGHT}`;
          return (
            <g key={i}>
              <polygon points={area} fill={color} fillOpacity={0.1} />
              <polyline
                points={line}
                fill="none"
                stroke={color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          );
        })}
      {ticks.map((tick) => (
        <rect
          key={tick.key}
          x={x(clip(tick.at)) - 1}
          y={SPARK_HEIGHT - TICK_HEIGHT}
          width={2}
          height={TICK_HEIGHT}
          fill={tick.color}
        />
      ))}
      {spans.map((s, i) => {
        const from = x(clip(s.from));
        const to = x(clip(s.to));
        return (
          <rect
            key={s.key}
            x={from - 1}
            y={SPARK_HEIGHT + SPAN_GAP + i * SPAN_ROW}
            width={Math.max(to - from, 0) + 2}
            height={2}
            rx={1}
            fill={SOURCE_COLOR.paid}
          />
        );
      })}
      <circle
        cx={x(lastKnown)}
        cy={y(lastValue)}
        r={4}
        fill={color}
        stroke="#FFFFFF"
        strokeWidth={2}
      />
    </svg>
  );
};

/** A muted heading over a chart or list, sentence case. */
export const VizHeading: React.FC<{ children: ReactNode; aside?: ReactNode }> = ({
  children,
  aside,
}) => (
  <div className="flex items-baseline justify-between gap-2 mb-2">
    <span className="text-[12px] font-medium text-gray-500">{children}</span>
    {aside != null && (
      <span className="text-[12px] text-gray-500 tabular-nums truncate">{aside}</span>
    )}
  </div>
);

export interface BarListRow {
  key: string;
  label: ReactNode;
  /** Null draws no bar: a value Google withholds, shown as `display`. */
  value: number | null;
  display: string;
  /** This row's bar colour, where it differs from the list's (paid). */
  color?: string;
}

/** Ranked rows, each bar's length its value against the largest. Bars are
 *  6px with a rounded end and a square start on the shared baseline. */
export const BarList: React.FC<{ rows: ReadonlyArray<BarListRow>; color: string }> = ({
  rows,
  color,
}) => {
  const max = Math.max(0, ...rows.map((row) => row.value ?? 0));
  return (
    <div className="space-y-1.5">
      {rows.map((row) => (
        <div
          key={row.key}
          className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_3.5rem] items-center gap-3 text-[12px]"
          title={`${typeof row.label === "string" ? row.label : row.key}: ${row.display}`}
        >
          <span className="truncate text-gray-700">{row.label}</span>
          <span className="h-[6px]">
            {row.value != null && max > 0 && (
              <span
                className="block h-full rounded-r-[3px]"
                style={{
                  width: `${Math.max((row.value / max) * 100, 2)}%`,
                  backgroundColor: row.color ?? color,
                }}
              />
            )}
          </span>
          <span
            className={`text-right tabular-nums ${
              row.value == null ? "text-gray-400" : "text-gray-700"
            }`}
          >
            {row.display}
          </span>
        </div>
      ))}
    </div>
  );
};

export interface QuietRow {
  label: string;
  value: string;
  /** Right of the value, such as a change pill. */
  aside?: ReactNode;
}

/** The figures a panel demotes under its chart: label left, value right,
 *  hairline-divided rows. */
export const QuietList: React.FC<{ rows: ReadonlyArray<QuietRow> }> = ({ rows }) => (
  <dl className="divide-y divide-gray-100 text-[12px]">
    {rows.map((row) => (
      <div key={row.label} className="flex items-center gap-3 py-1.5">
        <dt className="flex-1 min-w-0 truncate text-gray-500">{row.label}</dt>
        <dd className="tabular-nums text-gray-900">{row.value}</dd>
        {row.aside != null && <dd className="shrink-0">{row.aside}</dd>}
      </div>
    ))}
  </dl>
);

export interface MonthColumn {
  key: string;
  label: string;
  value: number | null;
  /** The month the report is about: drawn in the colour and labelled. */
  current: boolean;
  /** Hover text, such as "Oct to date: 48,400". */
  title: string;
}

/** A measure by month as columns (24px at most) on one baseline: the
 *  report's month in the source colour with its value on the cap, the
 *  others a pale step of it. A null month is a gap, never a zero column
 *  (DD-021). */
export const MonthColumns: React.FC<{ columns: ReadonlyArray<MonthColumn>; color: string }> = ({
  columns,
  color,
}) => {
  const max = Math.max(0, ...columns.map((c) => c.value ?? 0));
  if (max <= 0) return null;
  return (
    <ChartBox height={112} className="flex items-end gap-[2px] border-b border-gray-200">
      {columns.map((c) => (
        <div
          key={c.key}
          className="flex-1 min-w-0 h-full flex flex-col items-center justify-end"
          title={c.title}
        >
          {c.current && c.value != null && (
            <span className="text-[11px] font-medium text-gray-900 tabular-nums mb-1">
              {formatCompact(c.value)}
            </span>
          )}
          {c.value != null && (
            <span
              className="block w-full max-w-[24px] rounded-t-[4px]"
              // The tallest column takes 88/112 of the box, leaving room for the value on its cap.
              style={{
                height: `max(${((c.value / max) * 88) / 1.12}%, 2px)`,
                backgroundColor: color,
                opacity: c.current ? 1 : 0.28,
              }}
            />
          )}
        </div>
      ))}
    </ChartBox>
  );
};

/** The labels under MonthColumns, one per column. */
export const MonthLabels: React.FC<{ columns: ReadonlyArray<MonthColumn> }> = ({ columns }) => (
  <div className="flex gap-[2px] mt-1">
    {columns.map((c) => (
      <span
        key={c.key}
        className={`flex-1 min-w-0 text-center text-[10px] ${
          c.current ? "text-gray-700 font-medium" : "text-gray-400"
        }`}
      >
        {c.label}
      </span>
    ))}
  </div>
);

/** 48,400 as "48.4K", 1,284 as "1,284". */
export const formatCompact = (n: number): string =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 10_000
    ? `${(n / 1_000).toFixed(1)}K`
    : n.toLocaleString();

/** A share of a whole as one bar: two segments with a 2px surface gap and
 *  a legend under it, such as Maps vs Search. */
export const SplitBar: React.FC<{
  parts: [
    { label: string; value: number; color: string },
    { label: string; value: number; color: string },
  ];
}> = ({ parts }) => {
  const total = parts[0].value + parts[1].value;
  if (total <= 0) return null;
  const firstPct = Math.round((parts[0].value / total) * 100);
  return (
    <div>
      <div className="flex h-[8px] gap-[2px]">
        {parts.map((part, i) => (
          <span
            key={part.label}
            className={i === 0 ? "rounded-l-full" : "rounded-r-full"}
            style={{
              width: `${i === 0 ? firstPct : 100 - firstPct}%`,
              backgroundColor: part.color,
            }}
          />
        ))}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[12px] text-gray-600">
        {parts.map((part, i) => (
          <span key={part.label} className="flex items-center gap-1.5 tabular-nums">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: part.color }}
            />
            {part.label} {part.value.toLocaleString()}
            <span className="text-gray-400">
              {i === 0 ? firstPct : 100 - firstPct}%
            </span>
          </span>
        ))}
      </div>
    </div>
  );
};
